import React, { useState, useEffect, useMemo } from "react";
import Sidebar from "../components/Sidebar/index";
import { Outlet } from "react-router-dom";
import { useLocation } from "react-router-dom";
import { useIdleTimer } from "src/hooks/useIdleTimer";
import { LoginExpireModal } from "src/components/LoginExpireModal/LoginExpiremodal";
import { useSelector } from "react-redux";
import ShowTokenRefreshModal from "src/components/ShowTokenRefreshModal/ShowTokenRefreshModal";
import DeleteRestaurant from "src/components/DeleteRestaurant/DeleteRestaurant";
import { useAuth } from "src/contexts/auth-provider";
import DeleteCategory from "src/features/MenuCategories/components/DeleteCategory";
import DeleteCategoryItem from "src/components/DeleteRestaurant/DeleteCategoryItem";
import DeleteTable from "src/components/DeleteRestaurant/DeleteTable";
import DeleteCombo from "src/components/DeleteRestaurant/DeleteCombo";
import DeleteEmployee from "src/components/DeleteRestaurant/DeleteEmployee";
import Logo from "../components/common/Logo";
import RightHeaderSection from "../components/Header/RightHeaderSection";
import ToggleIcon from "../assests/images/dashboard/left_panel_close.png";
import ToggleIcon2 from "../assests/images/dashboard/left_panel_close 2.png";
import {
  MOBILE_NAV_MEDIA_QUERY,
  useMobileNavBreakpoint,
} from "src/hooks/useMobileNavBreakpoint";

const PATHNAME_TO_TITLE: Record<string, string> = {
  "/employee-profile": "User Profile",
  "/my-restaurants": "My Restaurants",
};

/** Breadcrumb-style header: section (muted) / page (bold) */
export type HeaderBreadcrumb = { section: string; page: string };

function getTitleFromPathname(pathname: string): string | HeaderBreadcrumb {
  if (PATHNAME_TO_TITLE[pathname]) return PATHNAME_TO_TITLE[pathname];
  if (pathname.includes("/restaurant-setting")) return "Settings";
  if (pathname.includes("/employee") && !pathname.includes("employee-profile")) return "Employees";
  if (pathname.includes("/restaurant-table")) return "Tables";
  if (pathname.includes("/customers")) return "Customers";
  if (pathname.includes("/promotions")) return "Promotions";
  if (pathname.includes("/orders")) return "Orders";
  if (pathname.includes("/OrderManagement")) return "Orders";
  if (pathname.includes("/active-orders")) return "Orders";
  if (pathname.includes("/menu/categories/")) return "Category";
  if (pathname.includes("/menu/items")) return "Items";
  if (pathname.includes("/menu")) return "Menu";
  if (pathname.includes("/modifier")) return "Modifiers";
  if (pathname.includes("/events")) return "Events";
  if (pathname.includes("/combo")) return "Combos";
  if (pathname.includes("/guide")) return { section: "Restaurant", page: "Operations guide" };
  // Reports: breadcrumb "Reports / [Page]" for second-level menus
  if (pathname.includes("/reports/tax")) return { section: "Reports", page: "Tax" };
  if (pathname.includes("/reports/employees")) return { section: "Reports", page: "Employees" };
  if (pathname.includes("/reports/payments")) return { section: "Reports", page: "Payments" };
  if (pathname.includes("/reports/items")) return { section: "Reports", page: "Menu Performance" };
  if (pathname.includes("/reports/sales-summary") || pathname.includes("/reports/sales")) return { section: "Reports", page: "Sales" };
  if (pathname.includes("/reports/overview")) return { section: "Reports", page: "Overview" };
  if (pathname.includes("/reports")) return "Reports";
  if (pathname.includes("/finance/overview")) return { section: "Finance", page: "Overview" };
  if (pathname.includes("/finance/cash-drawer")) return "Cash Drawer";
  if (pathname.includes("/finance/payments") || pathname.includes("/finance/transactions")) return "Payments";
  if (pathname.includes("/finance/payouts")) return { section: "Finance", page: "Payouts" };
  if (pathname.includes("/finance/disputes")) return { section: "Finance", page: "Disputes" };
  if (pathname.includes("/finance/payroll")) return "Payroll";
  if (pathname.includes("/finance")) return "Finance";
  return "Orders";
}

export const DefaultLayout = () => {
  const isMobileNav = useMobileNavBreakpoint();
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== "undefined"
      ? !window.matchMedia(MOBILE_NAV_MEDIA_QUERY).matches
      : true
  );
  const [pageTitle, setPageTitle]: any = useState("Orders");
  const { pathname } = useLocation();
  const { user } = useAuth();
  const auth = useSelector((state: any) => state.auth);
  const { hasStoreId } = useSelector((state: any) => state.restaurant);

  const { hasMenuCategoryId } = useSelector((state: any) => state.category);

  const { hasMenuItemId } = useSelector((state: any) => state.menuItem);

  const { hasTableId } = useSelector((state: any) => state.restaurantTable);
  const { hasComboId } = useSelector((state: any) => state.combo);
  const { hasEmployeeId } = useSelector((state: any) => state.employee);

  useIdleTimer();

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_NAV_MEDIA_QUERY);
    const onChange = () => {
      if (mq.matches) setSidebarOpen(false);
    };
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    if (isMobileNav) setSidebarOpen(false);
  }, [pathname, isMobileNav]);

  useEffect(() => {
    if (!isMobileNav || !sidebarOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isMobileNav, sidebarOpen]);

  /** Pixels below the visual viewport (Chrome iOS toolbar, etc.); drives --layout-mobile-bottom-inset in index.css */
  useEffect(() => {
    const root = document.documentElement;
    let raf = 0;
    const apply = () => {
      const vv = window.visualViewport;
      let raw = 0;
      if (vv) {
        const byInner = window.innerHeight - vv.offsetTop - vv.height;
        const byClient = root.clientHeight - vv.offsetTop - vv.height;
        raw = Math.max(byInner, byClient, 0);
      }
      const px = Math.min(Math.max(0, Math.round(raw)), 260);
      root.style.setProperty("--browser-bottom-overlay", `${px}px`);
    };
    const scheduleSync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(apply);
    };
    scheduleSync();
    const vv = window.visualViewport;
    vv?.addEventListener("resize", scheduleSync);
    vv?.addEventListener("scroll", scheduleSync);
    window.addEventListener("resize", scheduleSync);
    window.addEventListener("orientationchange", scheduleSync);
    return () => {
      cancelAnimationFrame(raf);
      vv?.removeEventListener("resize", scheduleSync);
      vv?.removeEventListener("scroll", scheduleSync);
      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("orientationchange", scheduleSync);
      root.style.removeProperty("--browser-bottom-overlay");
    };
  }, []);

  const hideLayout = [
    "/create-restaurant",
    "/upload-image",
    "/plans",
    "/stripe-connection",
    "/success",
    "/cancel",
    "/loginexpair",
  ].includes(pathname);

  const headerTitle = useMemo(
    (): string | HeaderBreadcrumb =>
      pathname === "/employee-profile"
        ? "User Profile"
        : getTitleFromPathname(pathname) || pageTitle,
    [pathname, pageTitle]
  );

  return (
    <div className="dark:bg-boxdark-2 dark:text-bodydark eb-app-shell-min h-full min-h-0 flex flex-col overflow-hidden bg-[#f8fafc]">
      {auth.showExpiryLoginModal && <LoginExpireModal />}
      {auth.isShowTokenRefreshModal && <ShowTokenRefreshModal />}

      {hasStoreId && <DeleteRestaurant restaurantId={hasStoreId} />}

      {hasMenuCategoryId && <DeleteCategory categoryId={hasMenuCategoryId} />}

      {hasMenuItemId && <DeleteCategoryItem menuItemId={hasMenuItemId} />}

      {hasTableId && <DeleteTable tableId={hasTableId} />}

      {hasComboId && <DeleteCombo comboId={hasComboId} />}
      {hasEmployeeId && <DeleteEmployee employeeId={hasEmployeeId} />}

      {!hideLayout && (
        <header
          className="sticky top-0 z-30 flex h-14 w-full shrink-0 items-center justify-between bg-white max-md:pl-[max(0.75rem,env(safe-area-inset-left,0px))] max-md:pr-[max(0.75rem,env(safe-area-inset-right,0px))] md:px-7 dark:bg-boxdark"
          style={{ boxShadow: "0 1px 0 rgba(0,0,0,0.04)" }}
        >
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <div className="flex h-7 items-center shrink-0">
              <Logo />
            </div>
            <button
              type="button"
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-lg hover:bg-black/5 md:h-9 md:w-9 dark:hover:bg-slate-800"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label="Toggle sidebar"
            >
              <img
                className="h-[18px] w-[18px]"
                src={sidebarOpen ? ToggleIcon : ToggleIcon2}
                alt=""
              />
            </button>
            <h1 className="truncate text-[20px] font-semibold tracking-[-0.02em] text-[#111827] dark:text-white flex items-center gap-1">
              {typeof headerTitle === "object" ? (
                <>
                  <span className="font-normal text-[#6b7280] dark:text-bodydark/80">{headerTitle.section}</span>
                  <span className="font-normal text-[#9ca3af] dark:text-bodydark/60">/</span>
                  <span className="font-semibold text-[#111827] dark:text-white">{headerTitle.page}</span>
                </>
              ) : (
                headerTitle
              )}
            </h1>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <RightHeaderSection user={user} />
          </div>
        </header>
      )}

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {!hideLayout && (
          <>
            {isMobileNav && sidebarOpen && (
              <button
                type="button"
                className="fixed inset-0 z-[35] bg-black/40 md:hidden"
                aria-label="Close menu"
                onClick={() => setSidebarOpen(false)}
              />
            )}
            <div className="max-md:contents md:shrink-0">
              <Sidebar
                isMobileNav={isMobileNav}
                sidebarOpen={sidebarOpen}
                setSidebarOpen={setSidebarOpen}
                setPageTitle={setPageTitle}
                hideTopRow
              />
            </div>
          </>
        )}

        <div
          className="relative flex flex-1 flex-col min-h-0 overflow-hidden"
          style={{ backgroundColor: "#F4F6F8" }}
        >
          <main
            className="flex flex-1 min-h-0 min-w-0 flex-col overflow-x-hidden overflow-y-auto max-md:[scroll-padding-bottom:var(--layout-mobile-bottom-inset)]"
          >
            <div
              className={
                !hideLayout
                  ? "w-full max-w-full flex-1 min-h-0 flex flex-col pt-3 max-md:pl-[max(0.75rem,env(safe-area-inset-left,0px))] max-md:pr-[max(0.75rem,env(safe-area-inset-right,0px))] max-md:pb-[var(--layout-mobile-bottom-inset)] md:pb-[10px] md:pl-5 md:pr-7"
                  : "flex flex-1 min-h-0 flex flex-col"
              }
            >
              <div className="flex flex-1 flex-col min-h-0 min-w-0 w-full">
                <Outlet />
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};
